import { useEffect, useState, type FC } from 'react'
import { useForm, Controller } from 'react-hook-form'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useGetEnrollmentHYCSFinancesData, useSubmitEnrollmentHYCSFinancesData } from '@egw/api'
import { Input, Button, Alert, AlertType, GenericError, RadioGroup } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { Logger } from '@egw/logger'
import { EnrollmentHYCSLayout } from '@egw/layouts'
import { useRouter } from '@egw/router'
import { formatInputToCurrency, removeCurrencySymbols } from '@egw/utils'
import { sendEvent } from '@egw/events'
import { WEALTH_ROUTES } from '@egw/constants'
import { PageHeader } from '../../../components'
import { useValidateRoute } from '../../../hooks/navigation/validate-route'
import { netWorthRangeItems } from '../../../constants/confirm-personal-info/confirm-personal-info'
import { EDataLayerEventWealth } from '../../../constants/events/EDataLayerEventWealth'
import { newPathIsDifferent } from '../../../utils/general/new-path-is-different'
import { ROUTES } from '../../../constants/routes/routes-generic'

const log = new Logger('Enrollment - HYCS - Finances')

interface FormData {
  income: string
  netWorth: string
}

export const FinancesPage: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const { navigate } = useRouter()

  const { data, isLoading, error } = useGetEnrollmentHYCSFinancesData()
  const { mutate, isLoading: isSubmitting, error: submitError } = useSubmitEnrollmentHYCSFinancesData()
  const [hasShownIntent, setHasShownIntent] = useState<boolean>(false)

  const applicationId = data?.__typename === 'SubmitEnrollmentSuccess' ? data.enrollmentData?.applicationId : ''

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors: formErrors, isValid },
  } = useForm({
    defaultValues: {
      income: '',
      netWorth: '',
    },
  })
  const income = watch('income')

  // Protect route
  useValidateRoute(data?.path)

  useEffect(() => {
    if (!hasShownIntent && income) {
      setHasShownIntent(true)
      sendEvent(EDataLayerEventWealth.BANKHYCHYS_FINANCES_INTENT, {
        applicationId,
        annualIncome: income,
      })
    }
  }, [applicationId, hasShownIntent, income])

  const onSubmit = handleSubmit(async (fData: unknown): Promise<void> => {
    const formData = fData as FormData
    if (isValid) {
      try {
        const response = await mutate({
          triggerEmailOtp: true,
          personalAnnualGrossIncome: removeCurrencySymbols(formData.income.toString()) || 0,
          personalNetWorthRange: formData.netWorth,
        })
        sendEvent(EDataLayerEventWealth.BANKHYCHYS_FINANCES_SUBMIT, {
          applicationId,
          annualIncome: formData.income,
        })
        // If path exists and is different
        if (response?.path && newPathIsDifferent(location.pathname, response.path)) {
          sendEvent(EDataLayerEventWealth.BANKHYCHYS_FINANCES_VERIFIED, {
            applicationId,
            annualIncome: formData.income,
          })
          navigate(response.path)
        } else {
          // If no path from BE, redirect to error
          if (!response?.path) {
            navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${ROUTES.ERROR.path}`)
          }
        }
      } catch (e) {
        log.error('Error submitting form', e)
      }
    }
  })

  return (
    <EnrollmentHYCSLayout title={t('hycs.finances.page_title')}>
      <div className="egw-container-enroll-hycs h-full flex flex-col relative md:justify-center justify-start items-start">
        {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
        {isLoading ? (
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        ) : null}

        {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
        {!isLoading && !error ? (
          <form
            onSubmit={(e) => void onSubmit(e)}
            className="w-full flex-1 flex flex-col justify-start lg:justify-center"
          >
            <PageHeader title={t('hycs.finances.title')} className="mb-xl" />
            <p className="typo-primary-base-semibold text-text-header mb-sm">
              {t('hycs.finances.annual_income_title')}
            </p>
            <p className="typo-primary-sm-regular text-text-meta mb-sm">{t('hycs.finances.annual_income_p1')}</p>
            <p className="typo-primary-sm-regular text-text-meta mb-sm">{t('hycs.finances.annual_income_p2')}</p>
            <p className="typo-primary-sm-regular text-text-meta mb-xl">{t('hycs.finances.annual_income_p3')}</p>

            <Controller
              name="income"
              control={control}
              rules={{
                required: t('common.required_field'),
              }}
              render={({ field }) => (
                <Input
                  {...field}
                  onChange={(e) => {
                    const { value } = e.target
                    let formattedValue =
                      formatInputToCurrency(value, true) === '0' ? '' : formatInputToCurrency(value, true) // format to currency
                    formattedValue = formattedValue.replaceAll('.', '') // remove cents
                    e.target.value = formattedValue // set internal value
                    field.onChange(e)
                    setValue('income', formattedValue)
                  }}
                  label={t('hycs.finances.annual_income')}
                  testId="annual-income-input"
                  error={formErrors.income?.message}
                  maxLength={12}
                />
              )}
            />

            <p className="typo-primary-base-semibold text-text-header mt-xl mb-md">
              {t('hycs.finances.your_net_worth')}
            </p>

            <Controller
              name="netWorth"
              control={control}
              rules={{
                required: t('common.required_field'),
              }}
              render={({ field: { ref, ...restField } }) => (
                <RadioGroup
                  {...restField}
                  onChange={(e) => {
                    restField.onChange(e)
                  }}
                  options={netWorthRangeItems.map((item) => ({
                    value: item.value,
                    label: t(`hycs.finances.personal_net_worth_range.${item.value}`),
                  }))}
                  name="personalNetWorthRange"
                  className="pb-sm"
                  testId="net-worth-radio"
                />
              )}
            />

            <div className="flex-1 md:hidden" />

            {submitError ? <Alert alertType={AlertType.Error} description={t('common.generic_error')} className="mt-sm" /> : null}

            {/* AUTO GENERATED COMPONENTS */}

            <Button
              type="submit"
              className="w-full md:w-min mt-xl"
              isDisabled={!isValid}
              isLoading={isSubmitting}
              testId="primaryAction_button"
            >
              {t('common.continue')}
            </Button>
          </form>
        ) : null}

        {/* This error handles the case when the initial data is not loaded, for example network error */}
        {error ? (
          <div className="flex-1 flex flex-col justify-start md:justify-center">
            <GenericError errorMessage={t('common.generic_error')} />
          </div>
        ) : null}
      </div>
    </EnrollmentHYCSLayout>
  )
}
